import React from 'react';
import { api, fileUrl } from '@/lib/api';
import { Megaphone } from 'lucide-react';

/**
 * SponsoredCard — in-feed sponsored ad slot. Renders a branded card with
 * a clear "Sponsored" label and an external CTA. Records click via
 * /api/ads/{id}/click which bills CPC budget on the backend.
 */
export function SponsoredCard({ ad }) {
  if (!ad || !ad.creative) return null;
  const c = ad.creative;
  const img = c.image_id ? fileUrl(c.image_id) : null;

  const handleClick = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/ads/${ad.ad_id || ad.id}/click`);
      const url = data?.cta_url || c.cta_url;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch {
      // Even on click-recording failure, still navigate so users aren't stuck.
      if (c.cta_url) window.open(c.cta_url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <article
      className="feed-card relative overflow-hidden rounded-2xl border border-[hsl(var(--gold)/0.55)] bg-[hsl(var(--panel))] text-[hsl(var(--on-panel))] shadow-[var(--shadow-elev-1)]"
      data-testid="sponsored-card"
      data-ad-id={ad.ad_id || ad.id}
    >
      <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full border border-[hsl(var(--gold)/0.55)] bg-[hsl(var(--app-card-2))] px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.14em] text-[hsl(var(--gold-dim))]" data-testid="sponsored-card-badge">
        <Megaphone className="h-3 w-3" /> Sponsored
      </span>
      {img && (
        <div className="aspect-video w-full overflow-hidden bg-[hsl(var(--app-card-2))]">
          <img src={img} alt="" className="h-full w-full object-cover" loading="lazy" />
        </div>
      )}
      <div className="p-4">
        {c.brand_name && (
          <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-[hsl(var(--gold))]">{c.brand_name}</div>
        )}
        <h3 className="mt-1 font-display text-lg font-semibold leading-tight" data-testid="sponsored-card-headline">{c.headline}</h3>
        {c.body && (
          <p className="mt-1 text-sm text-[hsl(var(--on-panel-muted,_var(--on-panel)))]">{c.body}</p>
        )}
        <div className="mt-3">
          <a
            href={c.cta_url}
            onClick={handleClick}
            className="inline-flex items-center justify-center rounded-xl border border-[hsl(var(--gold)/0.65)] bg-[hsl(var(--accent))] px-4 py-2 font-display text-sm font-medium text-white hover:bg-[hsl(var(--accent)/0.85)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]"
            data-testid="sponsored-card-cta"
          >
            {c.cta_text || 'Learn more'}
          </a>
        </div>
      </div>
    </article>
  );
}

export default SponsoredCard;
